import { useState, useEffect, useCallback } from 'react';
import { DayOperatingHours } from '@/components/common/WeeklyOperatingHours';

// 저장할 데이터 타입 정의
interface AccountInfo {
  email: string;
  phone: string;
  name: string;
  isCodeVerified: boolean;
}

interface BusinessInfo {
  companyName: string;
  representative: string;
  businessNumber: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
}

interface Agreements {
  terms: boolean;
  privacy: boolean;
  marketing: boolean;
}

interface StoreInfo {
  storeName: string;
  storePhone: string;
  address: string;
  detailAddress: string;
  foodTypes: string[];
  customFoodType: string;
  representativeMenus: string[];
  operatingHours: DayOperatingHours[];
}

interface SignupDataMetadata {
  createdAt: number;
  lastModified: number;
}

interface SellerSignupData {
  accountInfo: Partial<AccountInfo>;
  businessInfo: Partial<BusinessInfo>;
  agreements: Partial<Agreements>;
  storeInfo: Partial<StoreInfo>;
  metadata: SignupDataMetadata;
}

// 1시간 = 3600000ms
const EXPIRY_TIME = 60 * 60 * 1000;
const STORAGE_KEY = 'seller-signup-data';

// debounce를 위한 타이머
let saveTimer: NodeJS.Timeout | null = null;

export function useSellerSignupData() {
  const [data, setData] = useState<SellerSignupData>(() => {
    // 초기화 시 localStorage에서 데이터 로드
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsed: SellerSignupData = JSON.parse(savedData);
        
        // 20분 경과 체크
        if (Date.now() - parsed.metadata.lastModified > EXPIRY_TIME) {
          localStorage.removeItem(STORAGE_KEY);
          return createEmptyData();
        }
        
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load signup data from localStorage:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
    
    return createEmptyData();
  });

  // 빈 데이터 생성 함수
  function createEmptyData(): SellerSignupData {
    const now = Date.now();
    return {
      accountInfo: {},
      businessInfo: {},
      agreements: {},
      storeInfo: {},
      metadata: {
        createdAt: now,
        lastModified: now
      }
    };
  }

  // debounced save 함수
  const saveToLocalStorage = useCallback((newData: SellerSignupData) => {
    if (saveTimer) {
      clearTimeout(saveTimer);
    }
    
    saveTimer = setTimeout(() => {
      try {
        const dataToSave = {
          ...newData,
          metadata: {
            ...newData.metadata,
            lastModified: Date.now()
          }
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
      } catch (error) {
        console.error('Failed to save signup data to localStorage:', error);
      }
    }, 500); // 500ms debounce
  }, []);

  // 데이터 업데이트 함수들
  const updateAccountInfo = useCallback((updates: Partial<AccountInfo>) => {
    setData(prevData => {
      const newData = {
        ...prevData,
        accountInfo: { ...prevData.accountInfo, ...updates }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage]);

  const updateBusinessInfo = useCallback((updates: Partial<BusinessInfo>) => {
    setData(prevData => {
      const newData = {
        ...prevData,
        businessInfo: { ...prevData.businessInfo, ...updates }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage]);

  const updateAgreements = useCallback((updates: Partial<Agreements>) => {
    setData(prevData => {
      const newData = {
        ...prevData,
        agreements: { ...prevData.agreements, ...updates }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage]);

  const updateStoreInfo = useCallback((updates: Partial<StoreInfo>) => {
    setData(prevData => {
      const newData = {
        ...prevData,
        storeInfo: { ...prevData.storeInfo, ...updates }
      };
      saveToLocalStorage(newData);
      return newData;
    });
  }, [saveToLocalStorage]);

  // 모든 데이터 정리
  const clearAllData = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setData(createEmptyData());
  }, []);

  // 만료된 데이터 체크 (주기적 실행)
  useEffect(() => {
    const checkExpiry = () => {
      if (Date.now() - data.metadata.lastModified > EXPIRY_TIME) {
        clearAllData();
      }
    };

    // 5분마다 만료 체크
    const interval = setInterval(checkExpiry, 5 * 60 * 1000);
    
    return () => {
      clearInterval(interval);
      if (saveTimer) {
        clearTimeout(saveTimer);
      }
    };
  }, [data.metadata.lastModified, clearAllData]);

  return {
    // 데이터
    accountInfo: data.accountInfo,
    businessInfo: data.businessInfo,
    agreements: data.agreements,
    storeInfo: data.storeInfo,
    
    // 업데이트 함수들
    updateAccountInfo,
    updateBusinessInfo,
    updateAgreements,
    updateStoreInfo,
    
    // 유틸리티
    clearAllData,
    
    // 메타데이터
    hasStoredData: Object.keys(data.accountInfo).length > 0 || 
                   Object.keys(data.businessInfo).length > 0 || 
                   Object.keys(data.storeInfo).length > 0,
    lastModified: data.metadata.lastModified
  };
}

// 타입 export
export type { AccountInfo, BusinessInfo, Agreements, StoreInfo };